import React from 'react';
import { Col, Row, Typography } from 'antd';
import classNames from 'classnames';
import { first, last } from 'lodash-es';
import { FormattedMessage } from 'react-intl';

import { QuoteFlightItinerary } from './QuoteFlightItinerary';
import NoticeSection from './NoticeSection';
import { ShipmentDetailsLg, ShipmentDetailsSmall } from './ShipmentDetails';
import QuoteDetailMap from './components/QuoteDetailMap';
import AddressContactSection from './components/AddressContactSection';
import { QuoteSummaryOverviewSmall } from '../../../widgets/quotes/QuoteSummaryOverview';
import { useResponsiveQueries } from '../../../components/responsive/responsiveQueries';
import { TwoLinesSeparator } from '../../../components/layout/layoutElements';
import { ToBeDefined } from './components/NoValue';
import { AddressDisplaySmall } from './components/AddressDisplay';
import ContactInformationSimple from './components/ContactInformationSimple';
import DateTimeDisplay from './DateTimeDisplay';
import { DateFormatMedium } from '../../../utils/dateFormatting';
import FlightFormatted from '../../../components/flights/FlightFormatted';
import { QuoteDriveItinerary } from './QuoteDriveItinerary';
import { isQuoteDirectDrive } from '../../../components/quotes/quoteUtils';
import { useQuoteStyles } from '../../../styles/quote.styles';
import { useQuoteContentSmallStyles } from './QuoteContentSmall.styles';

const QuotePickupLg = ({ quoteDetail }) => {
  const firstFlight = first(quoteDetail.flights);
  return (
    <ShipmentDetailsLg span={12} title="shipmentDetail.pickupLocation">
      <Row style={{ marginTop: 10 }}>
        <Col span={14}>
          <p className="ShipmentDetailsTitle">
            <FormattedMessage id="book.newQuote.fromPickUp" />
          </p>
          <p>{quoteDetail.origin?.companyName}</p>
          <AddressDisplaySmall address={quoteDetail.origin?.address} />
        </Col>
        <Col span={10}>
          <Row>
            <Col span={24}>
              <div>
                <p className="ShipmentDetailsTitle text-uppercase">
                  <FormattedMessage id="job.readyDate" />
                </p>
                <p>
                  <DateTimeDisplay
                    dateTime={quoteDetail.pickupDateTime?.scheduled}
                    formatComponent={DateFormatMedium}
                  />
                </p>
              </div>
              {quoteDetail?.origin?.contact && (
                <div>
                  <p className="ShipmentDetailsTitle text-uppercase">
                    <FormattedMessage id="shipmentDetail.contact" />
                  </p>
                  <ContactInformationSimple contact={quoteDetail?.origin} />
                </div>
              )}
            </Col>
          </Row>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <TwoLinesSeparator className="full-width-separator" />
        </Col>
      </Row>

      <Row style={{ marginTop: 20 }}>
        <Col span={14}>
          {firstFlight && (
            <div>
              <p className="ShipmentDetailsTitle">
                <FormattedMessage id="book.newQuote.toDeliver" />
              </p>
              <p>
                <FlightFormatted flight={firstFlight} />
              </p>
            </div>
          )}
        </Col>
        <Col span={10}>
          <div>
            <p className="ShipmentDetailsTitle">
              <FormattedMessage id="quoteDetail.lockout.short" />
            </p>
          </div>
        </Col>
      </Row>
    </ShipmentDetailsLg>
  );
};
const QuoteDeliveryLg = ({ quoteDetail }) => {
  const lastFlight = last(quoteDetail.flights);
  return (
    <ShipmentDetailsLg title="shipmentDetail.deliveryLocation">
      <Row style={{ marginTop: 10 }}>
        <Col span={14}>
          <p className="ShipmentDetailsTitle">
            <FormattedMessage id="book.newQuote.fromPickUp" />
          </p>
          <p>
            <FlightFormatted flight={lastFlight} />
          </p>
        </Col>
        <Col span={10}>
          <Row>
            <Col span={24}>
              <div>
                <p className="ShipmentDetailsTitle">
                  <FormattedMessage id="quoteDetail.rcveta" />
                </p>
                <p>
                  <ToBeDefined />
                </p>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <TwoLinesSeparator className="full-width-separator" />
        </Col>
      </Row>
      <Row style={{ marginTop: 20 }}>
        <Col span={14}>
          <div>
            <p className="ShipmentDetailsTitle">
              <FormattedMessage id="book.newQuote.toDeliver" />
            </p>
            <p>{quoteDetail.destination?.companyName}</p>
            <AddressDisplaySmall address={quoteDetail.destination?.address} />
          </div>
        </Col>
        <Col span={10}>
          {quoteDetail?.destination?.contact && (
            <div>
              <p className="ShipmentDetailsTitle">
                <FormattedMessage id="shipmentDetail.contact" />
              </p>
              <ContactInformationSimple
                contact={quoteDetail?.destination?.contact}
              />
            </div>
          )}
        </Col>
      </Row>
    </ShipmentDetailsLg>
  );
};

const QuoteContentSmall = ({ quoteDetail }) => {
  const { styles } = useQuoteContentSmallStyles();
  const { styles: sharedStyles } = useQuoteStyles();
  const { lg } = useResponsiveQueries();
  const css = classNames('ShipmentDetails', styles.quoteContentSmall, {
    [styles.quoteContentLg]: lg,
  });
  const isDirectDrive = isQuoteDirectDrive(quoteDetail);
  return (
    <div className={css}>
      <Row className="hide-lg-and-bigger">
        <Col span={24}>
          <TwoLinesSeparator className="full-width-separator" />
          <Typography.Title
            level={3}
            className={classNames(
              'text-uppercase',
              sharedStyles.shipmentInfoTitle
            )}
          >
            <FormattedMessage id="shipmentDetail.shipmentDetails" />
          </Typography.Title>
          <TwoLinesSeparator className="full-width-separator" />
        </Col>
      </Row>
      <Row gutter={lg ? [20, 20] : []}>
        {lg ? (
          <QuotePickupLg quoteDetail={quoteDetail} />
        ) : (
          <ShipmentDetailsSmall
            title="shipmentDetail.pickupLocation"
            subtitle="book.quote.flightInformation.ready"
            dateTime={quoteDetail.pickupDateTime?.scheduled}
          >
            <AddressContactSection
              companyName={quoteDetail.origin?.companyName}
              address={quoteDetail.origin?.address}
              contact={quoteDetail?.origin?.contact}
            />
          </ShipmentDetailsSmall>
        )}

        {lg ? (
          <QuoteDeliveryLg quoteDetail={quoteDetail} />
        ) : (
          <ShipmentDetailsSmall
            className={styles.shipmentDetailsDeliveryCol}
            title="shipmentDetail.deliveryLocation"
            subtitle="job.deadlineDate.short"
            dateTime={quoteDetail?.deliveryDateTime?.scheduled}
          >
            <AddressContactSection
              companyName={quoteDetail.destination?.companyName}
              address={quoteDetail.destination?.address}
              contact={quoteDetail.destination?.contact}
            />
          </ShipmentDetailsSmall>
        )}
      </Row>

      <div className="hide-lg-and-bigger">
        <QuoteSummaryOverviewSmall quote={quoteDetail} />
      </div>

      <Row>
        <Col span={24}>
          {isDirectDrive ? (
            <QuoteDriveItinerary quote={quoteDetail} />
          ) : (
            <QuoteFlightItinerary flights={quoteDetail.flights} />
          )}
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <QuoteDetailMap quote={quoteDetail} />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <NoticeSection />
        </Col>
      </Row>
    </div>
  );
};

export default QuoteContentSmall;
